import type {
    Advantage,
    NavLink,
    Pillar,
    Spec
} from '@/types'

export const NAV_LINKS: NavLink[] = [
    { label: 'About', href: '#about' },
    { label: 'Fleet', href: '#fleet' },
    { label: 'Advantages', href: '#advantages' },
    { label: 'Global', href: '#global' }
]

export const HERO_COPY = {
    lead: ['Precision', 'in motion'],
    counter: ['Discretion', 'at altitude'],
    asideTitle: ['Depart at will,', 'arrive at ease'],
    asideText: "Every flight is shaped around your schedule, your privacy and your comfort, so the only thing left for you to do is arrive.",
    cueLeft: 'Scroll to board',
    cueRight: 'Begin the ascent'
}

export const STATEMENT = {
    brand: 'MACHGO',
    text: "is a private aviation house for people who measure a journey in moments, not miles. Every flight is planned around one person: you."
}

export const PILLARS: Pillar[] = [
    {
        title: 'Direct access',
        text: "One conversation opens the whole sky. Tell us where and when, and every detail from the first call to the last step onto the tarmac is handled for you."
    },
    {
        title: 'Your time, returned',
        text: "Fly on your schedule, from the airport closest to your door to the one closest to where you need to be. The hours you save are yours again."
    },
    {
        title: 'Precision in every detail',
        text: "Routes, crews, catering and ground transfers are planned with the same care, so the journey feels effortless because nothing is left to chance."
    },
    {
        title: 'Global reach, personal touch',
        text: "Wherever the day takes you, the same small team knows your preferences, your pace and the way you like to travel."
    }
]

export const CLOUD_LINE = "Above the weather, the world goes quiet."

export const FLEET_COPY = {
    headline: ['Fly the', 'flagship'],
    eyebrow: 'The MACHGO flagship',
    title: 'Ultra long range',
    claim: "Engineered for the long way round, in total comfort.",
    label: 'Room to dine, work and sleep',
    text: "A quiet, stand-up cabin arranged as three living areas. Wide oval windows fill it with daylight, and a full galley means every meal is prepared on board, not simply reheated."
}

export const SPECS: Spec[] = [
    { label: 'Category', value: 'Ultra long range' },
    { label: 'Range', value: 'Intercontinental, nonstop' },
    { label: 'Cabin', value: 'Three living areas' },
    { label: 'Rest', value: 'Fully flat beds' },
    { label: 'Light', value: 'Panoramic oval windows' },
    { label: 'Galley', value: 'Full service' }
]

export const ADVANTAGES_COPY = {
    eyebrow: 'A better way to fly',
    title: ['Every detail,', 'considered'],
    intro: "The difference is in the parts of a journey nobody else thinks about."
}

export const ADVANTAGES: Advantage[] = [
    {
        id: 'pets',
        title: 'Pets welcome',
        text: "Your companions travel in the cabin beside you, never in the hold. We arrange the paperwork, the comforts and a quiet corner of their own.",
        image: '/images/aerive/advantage-pets-2.png',
        alt: "A black cat asleep on a black leather seat beside an oval window at dusk"
    },
    {
        id: 'concierge',
        title: '24/7 concierge',
        text: "One team, any hour, any time zone. A single call covers the flight, the car, the table and the hotel, even at a moment's notice.",
        image: '/images/aerive/advantage-concierge.png',
        alt: "A concierge on the phone in a private terminal at night, a black jet waiting outside",
        crop: 1.08
    },
    {
        id: 'dining',
        title: 'Onboard dining',
        text: "Menus composed around your taste and your route, from a light breakfast to a full tasting menu, paired and served at altitude.",
        image: '/images/aerive/advantage-dining-2.png',
        alt: "Coffee being poured beside a pastry service on a black lacquered cabin table"
    },
    {
        id: 'boarding',
        title: 'Effortless boarding',
        text: "Wherever the airport allows, your car drives right up to the aircraft steps. No terminals, no queues, just a few steps from the door to your seat.",
        image: '/images/aerive/advantage-boarding.png',
        alt: "The open door of a black car framing a black private jet with its airstair lowered at sunset"
    }
]

export const STATUS_ITEMS = [
    { label: 'Concierge', value: 'Around the clock' },
    { label: 'Coverage', value: 'Worldwide' }
]

export const GLOBAL_COPY = {
    word: 'Global',
    kicker: 'Fly anywhere',
    title: "Fly anywhere with total comfort and control.",
    text: "From a weekend in Mykonos to a board meeting in Singapore, MACHGO plans the route, the permits and the ground transfers, so the world feels a little closer."
}

export const DESTINATIONS: string[] = [
    'London',
    'Geneva',
    'Nice',
    'Mykonos',
    'Dubai',
    'Marrakech',
    'Cape Town',
    'New York',
    'Aspen',
    'Miami',
    'Los Angeles',
    'Mexico City',
    'São Paulo',
    'Tokyo',
    'Singapore',
    'Hong Kong',
    'Sydney',
    'Paris',
    'Milan',
    'St. Moritz',
    'Maldives',
    'Riyadh'
]

export const INQUIRY_COPY = {
    eyebrow: 'For inquiries',
    title: ['Tell us where.', 'We handle the rest.'],
    text: "Share your route and your dates. A member of the MACHGO flight team replies personally with options for your journey."
}
